import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  Link,
} from "@beweegbaas/ui";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState, type ReactNode } from "react";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { toast } from "@beweegbaas/ui";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page, useInHeaderMobileActions } from "#/components/page";
import { SubmitButton } from "#/components/submit-button";
import { TabsNavigation } from "#/components/tabs-navigation";
import { LessonStatus } from "#/modules/les.docent-gestuurd/components/les-status";
import { getLesTab, hasLesTab } from "#/modules/les.docent-gestuurd/tabs";
import { AdministratiefLabel } from "#/modules/les.planning/components/les-tegel";
import { getStepTo } from "#/modules/les.planning/preparation-steps";
import { PLANNING_QUERY_KEY } from "#/modules/les.planning/query.planning";
import {
  annuleerLesFn,
  getTeacherLessonData,
  lesAfrondenFn,
  lesAfrondenPrecheckFn,
  startLes,
} from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { lockStudentLesson } from "#/modules/les.leerling-gestuurd/lesson-student.functions";
import { getStartLessonFiltersFromLesEditData } from "#/modules/les.start-flow/filters";
import { getGroepeerTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { incrementProfileProperty, op } from "#/modules/util.instrumentation/op";
import type { components } from "#/schema";
import { isApiFailure } from "#/modules/util.errors/translate";
import { tenantQueryFilter } from "#/modules/util.fetch-client/tenant-query-key";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher",
)({
  loader: async ({ params, location }) => {
    const result = await getTeacherLessonData({ data: { lesId: params.lesson } });

    if (result.notFound || !result.lesson) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lessons/today",
        params: { locale: params.locale, schoolyear: params.schoolyear, school: params.school },
      });
    }

    // Administratieve lessen have no scores tab; portal redirects scores → administration. What
    // is open over the scores, the edit dialog, opens over the administration instead.
    if (result.lesson.isAdministratieLes && location.pathname.includes("/teacher/scores")) {
      throw redirect({ href: location.href.replace("/teacher/scores", "/teacher/administration") });
    }

    return { lesson: result.lesson, lesEditData: result.lesEditData };
  },
  component: TeacherLessonLayout,
  errorComponent: TeacherLessonError,
});

function TeacherLessonLayout() {
  const { lesson, lesEditData } = Route.useLoaderData();
  const { locale, schoolyear, school, lesson: lessonId } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();

  // The planning caches its lessen outside the router, so a router invalidate alone leaves the
  // week showing the les as it was before it started, was cancelled or finished.
  async function refresh() {
    void queryClient.invalidateQueries(
      tenantQueryFilter({ school, schoolyear }, PLANNING_QUERY_KEY),
    );
    await router.invalidate({ sync: true });
  }

  const startLesFn = useServerFn(startLes);
  const lockStudentLessonFn = useServerFn(lockStudentLesson);
  const annuleerFn = useServerFn(annuleerLesFn);
  const precheckFn = useServerFn(lesAfrondenPrecheckFn);
  const afrondenFn = useServerFn(lesAfrondenFn);

  const [precheckData, setPrecheckData] = useState<
    components["schemas"]["LesAfrondenPrecheckResponse"] | null
  >(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const formatter = useDateFormatter({ month: "long", weekday: "long", day: "numeric" });
  // Editing opens over the tab that is open, which stays as it is underneath, the scores with
  // their colours as they are; over one the les does not have, it opens over its first tab.
  const openTab = useLocation({ select: (location) => getLesTab(location.pathname) });
  const tab =
    openTab && hasLesTab(openTab, lesson)
      ? openTab
      : lesson.isAdministratieLes
        ? "administration"
        : "scores";
  const hideScoreColors = useSearch({
    from: "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher/scores",
    shouldThrow: false,
    select: (search) => search.hideScoreColors,
  });

  async function handleStart() {
    setIsBusy(true);
    try {
      await startLesFn({ data: { lesId: lessonId } });
      await refresh();
      toast.success("Les gestart");
      void op.track("lesson_started", { lesson_id: lessonId, source: "planned" });
    } finally {
      setIsBusy(false);
    }
  }

  async function handleAnnuleer() {
    setIsBusy(true);
    try {
      await annuleerFn({ data: { lesId: lessonId } });
      await refresh();
      toast.success("Les geannuleerd");
    } finally {
      setIsBusy(false);
    }
  }

  async function finishLesson() {
    await afrondenFn({ data: { lesId: lessonId } });
    await refresh();
    toast.success("Les afgerond");
    void op.track("lesson_completed", { lesson_id: lessonId });
    incrementProfileProperty("lessons_completed");
  }

  async function handleAfronden() {
    setIsBusy(true);
    try {
      const result = await precheckFn({ data: { lesId: lessonId } });
      if (!result || isApiFailure(result)) return;
      if (result.hasUnfinishedResultaten) {
        setPrecheckData(result);
        setShowWarning(true);
        return;
      }
      await finishLesson();
    } finally {
      setIsBusy(false);
    }
  }

  const tabParams = { locale, schoolyear, school, lesson: lessonId };

  return (
    <>
      {precheckData?.hasUnfinishedResultaten && (
        <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{m["common.attention"]()}</AlertDialogTitle>
              <AlertDialogDescription>
                {m["les.actief.afronden_warning.confirm_message"]()}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <ul
              className="bg-muted text-muted-foreground mt-3 flex flex-col
                gap-1 divide-y divide-black/5 rounded-md p-2 text-sm"
            >
              {precheckData.beweegactiviteiten.map((activity) => (
                <li
                  key={activity.beweegactiviteitId}
                  className="flex flex-wrap items-baseline justify-between
                      gap-3 not-first:pt-1 not-last:pb-1"
                >
                  {activity.name}{" "}
                  <span className="shrink-0">
                    (
                    {m["les.actief.afronden_warning.beweegactiviteiten.results_count"]({
                      count: activity.leerlingenWithoutResultaat.length,
                    })}
                    )
                  </span>
                </li>
              ))}
              {precheckData.sociaalEmotioneelThemas.map((theme) => (
                <li
                  key={theme.sociaalEmotioneelThemaId}
                  className="flex flex-wrap items-baseline justify-between
                      gap-3 not-first:pt-1 not-last:pb-1"
                >
                  {theme.name}{" "}
                  <span className="shrink-0">
                    (
                    {m["les.actief.afronden_warning.sociaal.results_count"]({
                      count: theme.leerlingenWithoutResultaat.length,
                    })}
                    )
                  </span>
                </li>
              ))}
            </ul>

            <AlertDialogFooter>
              <AlertDialogCancel variant="outline" size="default">
                {m["common.actions.cancel"]()}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  setShowWarning(false);
                  await finishLesson();
                }}
              >
                {m["les.actief.toch_afronden"]()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <Page>
        <Page.Header className="justify-between text-sm">
          <div className="flex flex-col items-start gap-x-4 2xl:flex-row 2xl:items-center">
            <Page.Title className="flex items-center gap-2 text-2xl font-black">
              {lesson.klasName}
              {/* On a phone the header shows only the title's children, without its flex and gap. */}
              <span className="inline-flex items-center gap-2 align-middle max-md:ml-2">
                <LessonStatus status={lesson.status} />
                {lesson.isAdministratieLes && <AdministratiefLabel />}
              </span>
            </Page.Title>
            <div className="text-muted-foreground flex flex-wrap items-center gap-x-2">
              {m["les.actief.groepen_count"]({
                aantalGroepen: lesson.aantalGroepen,
              })}
              {lesson.aantalAanwezig != null && (
                <>
                  <span className="opacity-50">/</span>
                  {m["les.actief.aanwezig_count"]({
                    aanwezig: lesson.aantalAanwezig,
                    totaal: lesson.aantalLeerlingen,
                  })}
                </>
              )}
              <span className="opacity-50">/</span>
              {!lesson.isAdministratieLes && (
                <>
                  {m["les.actief.gegroepeerd"]({
                    groepeerType: getGroepeerTypeTranslation(lesson.groepeerType),
                  })}
                  <span className="opacity-50">/</span>
                </>
              )}
              {formatter.format(new Date(lesson.date))}
              {lesson.canBeEdited && lesEditData && (
                <>
                  <span className="opacity-50">/</span>
                  <Link
                    variant="secondary"
                    underline={true}
                    to={getStepTo(tab, "activities")}
                    params={tabParams}
                    search={{
                      ...getStartLessonFiltersFromLesEditData(lesEditData, lessonId),
                      hideScoreColors,
                    }}
                  >
                    {m["les.docent.bewerken"]()}
                  </Link>
                </>
              )}
            </div>
          </div>
          {(lesson.status === "Actief" || lesson.canBeStarted) && (
            <Page.HeaderActions className="ml-0">
              {lesson.status === "Actief" && (
                <Page.Header.MainAction>
                  <Button
                    size="xl"
                    variant="default"
                    onClick={async () => {
                      // Lock the session via POST BEFORE entering the student view (Set-Cookie
                      // only persists from POST server fns; portal locked in the student loader).
                      // Passes THIS les as the lock target so the guard can redirect to it without
                      // consulting the eventually-consistent activeLes metadata (dex q7hqg0da).
                      await lockStudentLessonFn({ data: { lesson: lessonId, school } });
                      await router.navigate({
                        to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/student",
                        params: { locale, schoolyear, school, lesson: lessonId },
                      });
                    }}
                  >
                    {m["les.docent.student_gestuurd"]()}
                    <IconMaterialSymbolsArrowRightAltRounded />
                  </Button>
                </Page.Header.MainAction>
              )}

              <Page.Header.SecondaryActions>
                {lesson.status === "Actief" && (
                  <SecondaryActionsGroup>
                    <SubmitButton
                      type="button"
                      variant="secondary"
                      isPending={isBusy}
                      onClick={handleAnnuleer}
                    >
                      {m["les.actief.annuleer"]()}
                    </SubmitButton>
                    <SubmitButton
                      type="button"
                      variant="secondary"
                      isPending={isBusy}
                      onClick={handleAfronden}
                    >
                      {m["les.actief.afronden"]()}
                    </SubmitButton>
                  </SecondaryActionsGroup>
                )}
              </Page.Header.SecondaryActions>

              {lesson.canBeStarted && (
                <Page.Header.MainAction>
                  <SubmitButton
                    type="button"
                    variant="default"
                    size="xl"
                    isPending={isBusy}
                    onClick={handleStart}
                  >
                    {m["les.start.cta"]()}
                    <IconMaterialSymbolsArrowRightAltRounded />
                  </SubmitButton>
                </Page.Header.MainAction>
              )}
            </Page.HeaderActions>
          )}
        </Page.Header>

        <Page.Subnavigation>
          <TabsNavigation className="min-w-0 grow">
            <TabsNavigation.List aria-label={m["les.start.beweegactiviteitenreeksen"]()}>
              {!lesson.isAdministratieLes && (
                <TabsNavigation.Link
                  to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
                  params={tabParams}
                >
                  {m["les.docent.tabs.scores"]()}
                </TabsNavigation.Link>
              )}
              <TabsNavigation.Link
                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/administration"
                params={tabParams}
              >
                {m["les.docent.tabs.administratie"]()}
              </TabsNavigation.Link>

              <TabsNavigation.Link
                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/sport-participation"
                params={tabParams}
              >
                {m["sportdeelname.title"]()}
              </TabsNavigation.Link>

              {!lesson.isAdministratieLes && (
                <TabsNavigation.Link
                  to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/mediakaarten"
                  params={tabParams}
                >
                  {m["les.docent.tabs.mediakaarten"]()}
                </TabsNavigation.Link>
              )}

              {!lesson.isAdministratieLes && (
                <TabsNavigation.Link
                  to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/les-info"
                  params={tabParams}
                >
                  {m["les.docent.tabs.lesinfo"]()}
                </TabsNavigation.Link>
              )}
              <TabsNavigation.Link
                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/notities"
                params={tabParams}
              >
                {m["common.labels.notities"]()}
              </TabsNavigation.Link>
            </TabsNavigation.List>
          </TabsNavigation>
        </Page.Subnavigation>

        <Page.Body
          data-testid="les-tab"
          // These tabs start with their table, which meets the tabs without a gap.
          className={cn(
            (openTab === "administration" || openTab === "sport-participation") && "mt-0",
          )}
        >
          <Outlet />
        </Page.Body>
      </Page>
    </>
  );
}

/** In a row next to the main action; in the drawer of a phone stacked, each at full width. */
function SecondaryActionsGroup({ children }: { children: ReactNode }) {
  const inDrawer = useInHeaderMobileActions();
  return (
    <div className={cn("flex gap-2", inDrawer ? "flex-col items-stretch" : "items-center")}>
      {children}
    </div>
  );
}

function TeacherLessonError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
